import { useFormatters, useTranslation } from "@ferrivia/i18n";
import {
  ClipboardCheck,
  FlaskConical,
  ListChecks,
  ShoppingBag,
  Signpost,
  TestTube,
  UserPlus,
  type LucideIcon,
} from "lucide-react";
import { useActionLabels } from "../../actions/kinds";
import type { FeedEntry } from "./types";

export type CardEntry = Exclude<FeedEntry, { kind: "message" }>;

/* Copy for one timeline entry, rendered here rather than on the server.
 *
 * The API sends structured facts per kind (see packages/api-contract's
 * timeline.ts); the words, the locale's date and money formatting, and the
 * plurals are this console's, through @ferrivia/i18n like every other clinic
 * string. Nothing in here interprets a measured value — a lab entry carries
 * no value to interpret, only provenance, dates, source and a count.
 *
 * Enum-shaped fields arrive as plain strings (lenient read). Every lookup
 * below therefore falls back to the raw value rather than rendering an empty
 * string if a newer server sends something this build has not seen.
 */

export type IconTint = "brand" | "lab";

export interface EntryContent {
  Icon: LucideIcon;
  /** Which strand the entry belongs to, never anything about a measurement —
      the same rule chart series colour follows in @ferrivia/lab-ui. */
  tint: IconTint;
  title: string;
  /** Detail lines, rendered one under the other. */
  lines: string[];
  /** The answer this declaration replaced, as a quieter footnote line. */
  supersede?: string;
  /** Set on entries whose numbers live on the Labs tab. */
  opensLabs?: boolean;
  /** Set on action cards: the action "Open in plan" goes to. */
  opensAction?: string;
}

export function useEntryContent(): (entry: CardEntry) => EntryContent {
  const { t } = useTranslation("clinic");
  const { tag, currencyEur } = useFormatters();
  const { titleLabel, formatDay: formatCheckInDay, runsLabel } = useActionLabels();

  // collectedOn is "YYYY-MM-DD" with no time component, so it is formatted in
  // UTC — the same reasoning as LabsPane's formatCollectedOn, which a browser
  // west of Finland would otherwise show a day early.
  function formatDay(isoDate: string): string {
    return new Intl.DateTimeFormat(tag, { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" }).format(
      new Date(`${isoDate}T00:00:00Z`),
    );
  }

  function formatTimestampDay(iso: string): string {
    return new Intl.DateTimeFormat(tag, { day: "numeric", month: "short", year: "numeric" }).format(new Date(iso));
  }

  function formatMoney(totalCents: number, currency: string): string {
    const amount = totalCents / 100;
    return currency.toLowerCase() === "eur"
      ? currencyEur.format(amount)
      : new Intl.NumberFormat(tag, { style: "currency", currency: currency.toUpperCase() }).format(amount);
  }

  function authMethodLabel(method: string): string {
    switch (method) {
      case "ftn":
        return t("timelineFeed.authMethod.ftn", "Finnish Trust Network");
      case "google":
        return t("timelineFeed.authMethod.google", "Google");
      case "fake":
        return t("timelineFeed.authMethod.fake", "dev login");
      case "email":
        return t("timelineFeed.authMethod.email", "Email code");
      default:
        return method;
    }
  }

  function channelLabel(channel: string): string {
    switch (channel) {
      case "webstore":
        return t("timelineFeed.channel.webstore", "Webstore");
      case "event":
        return t("timelineFeed.channel.event", "Event");
      case "rautapaiva":
        return t("timelineFeed.channel.rautapaiva", "Rautapäivä");
      case "imported":
        return t("timelineFeed.channel.imported", "Imported");
      default:
        return channel;
    }
  }

  function collectionMethodLabel(method: string): string {
    switch (method) {
      case "finger_prick":
        return t("timelineFeed.collectionMethod.fingerPrick", "Finger-prick");
      case "venous":
        return t("timelineFeed.collectionMethod.venous", "Venous draw");
      default:
        return method;
    }
  }

  // The choice as staff read it. Deliberately not the client's own Finnish
  // wording: that copy lives in the client app's catalog and would drift from
  // a copy kept here, and misquoting someone's declaration is worse than
  // summarising it.
  function choiceLabel(choice: string): string {
    switch (choice) {
      case "yes":
        return t("timelineFeed.intent.choiceYes", "Wants to raise iron levels");
      case "no":
        return t("timelineFeed.intent.choiceNo", "Not raising iron for now");
      default:
        return choice;
    }
  }

  return function entryContent(entry: CardEntry): EntryContent {
    switch (entry.kind) {
      case "signup":
        return {
          Icon: UserPlus,
          tint: "brand",
          title: t("timelineFeed.signup.title", "Account created"),
          // A webstore buyer's account is created by the checkout and has no
          // sign-in method yet, so this says what happened instead of leaving
          // the line blank.
          lines:
            entry.provisionedVia === "purchase"
              ? [t("timelineFeed.signup.atCheckout", "Created at checkout · no sign-in method yet")]
              : entry.authMethod
                ? [
                    t("timelineFeed.signup.viaMethod", {
                      method: authMethodLabel(entry.authMethod),
                      defaultValue: "Signed up via {{method}}",
                    }),
                  ]
                : [],
        };

      case "lab_purchase": {
        const items = entry.items.map((item) =>
          item.quantity > 1
            ? t("timelineFeed.labPurchase.itemQuantity", {
                name: item.productName,
                quantity: item.quantity,
                defaultValue: "{{name}} ×{{quantity}}",
              })
            : item.productName,
        );
        return {
          Icon: ShoppingBag,
          tint: "brand",
          title: t("timelineFeed.labPurchase.title", "Lab panel purchased"),
          lines: [
            items.join(" · "),
            [channelLabel(entry.channel), entry.orderNumber, formatMoney(entry.totalCents, entry.currency)].join(" · "),
          ],
        };
      }

      case "specimen_collected":
        return {
          Icon: TestTube,
          tint: "lab",
          title: t("timelineFeed.specimen.title", "Sample collected"),
          lines: [
            [
              collectionMethodLabel(entry.collectionMethod),
              channelLabel("rautapaiva"),
              entry.collectingOrg,
              formatDay(entry.collectedOn),
            ]
              .filter((part): part is string => Boolean(part))
              .join(" · "),
          ],
        };

      case "lab_result": {
        // labCode, not provenance: it is set exactly when a lab actually
        // processed the draw, which is what decides whether a laboratory may
        // be named. Same rule as LabsPane and @ferrivia/lab-ui's rangeCopy.
        const labProcessed = entry.labCode !== null;
        const source = labProcessed
          ? t("timelineFeed.labResult.lab", "Arvoterveys")
          : entry.sourceLab
            ? // A client typed this in, so it is quoted rather than presented
              // as a laboratory we know.
              `“${entry.sourceLab}”`
            : t("timelineFeed.labResult.selfReported", "Self-reported");
        return {
          Icon: FlaskConical,
          tint: "lab",
          title: labProcessed
            ? t("timelineFeed.labResult.titleOnFile", "Results on file")
            : t("timelineFeed.labResult.titleUploaded", "Result uploaded by client"),
          lines: [
            [
              source,
              t("timelineFeed.labResult.drawn", {
                date: formatDay(entry.collectedOn),
                defaultValue: "draw {{date}}",
              }),
              t("timelineFeed.labResult.measurements", {
                count: entry.analyteCount,
                defaultValue: "{{count}} measurements",
              }),
            ].join(" · "),
          ],
          opensLabs: true,
        };
      }

      case "intent_declared":
        return {
          Icon: Signpost,
          tint: "brand",
          title: entry.supersedes
            ? t("timelineFeed.intent.titleChanged", "Day-0 answer changed")
            : t("timelineFeed.intent.titleAnswered", "Day-0 question answered"),
          lines: [choiceLabel(entry.choice)],
          supersede: entry.supersedes
            ? t("timelineFeed.intent.supersedes", {
                choice: choiceLabel(entry.supersedes.choice),
                date: formatTimestampDay(entry.supersedes.declaredAt),
                defaultValue: "Replaces “{{choice}}” from {{date}}",
              })
            : undefined,
        };

      case "action": {
        const { action, previous } = entry;
        const byline = `${action.authorName}, ${titleLabel(action.authorTitle)}`;
        const checkIn = action.checkInOn
          ? t("timelineFeed.action.checkInOn", {
              date: formatCheckInDay(action.checkInOn),
              defaultValue: "check-in {{date}}",
            })
          : null;
        return {
          Icon: ListChecks,
          tint: "brand",
          title: previous
            ? t("timelineFeed.action.changed", {
                from: previous.title,
                to: action.title,
                defaultValue: "Action changed: {{from}} → {{to}}",
              })
            : t("timelineFeed.action.started", { title: action.title, defaultValue: "Action started: {{title}}" }),
          lines: [
            [byline, ...(previous ? [] : [runsLabel(action.runsForWeeks)]), checkIn]
              .filter((part): part is string => Boolean(part))
              .join(" · "),
          ],
          supersede: action.changeReason
            ? t("timelineFeed.action.reason", {
                reason: action.changeReason,
                defaultValue: "Reason: {{reason}}",
              })
            : undefined,
          opensAction: action.id,
        };
      }

      case "action_check_in": {
        const { checkIn, action } = entry;
        const outcome =
          checkIn.outcome === "continue"
            ? t("timelineFeed.action.continued", "continued")
            : checkIn.outcome === "stop"
              ? t("timelineFeed.action.stopped", "stopped")
              : t("timelineFeed.action.completed", "completed");
        const next = checkIn.nextCheckInOn
          ? t("timelineFeed.action.nextCheckIn", {
              date: formatCheckInDay(checkIn.nextCheckInOn),
              defaultValue: "next check-in {{date}}",
            })
          : null;
        return {
          Icon: ClipboardCheck,
          tint: "brand",
          title: t("timelineFeed.action.checkInTitle", {
            title: action.title,
            outcome,
            defaultValue: "Check-in: {{title}} · {{outcome}}",
          }),
          lines: [
            [`${checkIn.authorName}, ${titleLabel(checkIn.authorTitle)}`, next]
              .filter((part): part is string => Boolean(part))
              .join(" · "),
          ],
          opensAction: action.id,
        };
      }
    }
  };
}
